import { defineStore } from "pinia";
import type { IContentReadRepository } from "~/interfaces/IContentReadRepository";
import { ContentReadRepository } from "~/repositories/ContentReadRepository";
import { ContentReadInfo } from "~/entities/ContentReadInfo";
import { CONTENT_TYPE_LIST } from "~/consts/contentsConfig";

export const useContentReadStore = defineStore("ContentReadStore", () => {
  const repository: IContentReadRepository = new ContentReadRepository();

  // プランごと・コンテンツタイプごとの未読チェック情報
  const latestContentsList = ref<{ [planId: string]: { [contentsType: string]: ContentReadInfo } }>({});

  // Firestoreから未読情報を取得（Firestoreキャッシュを活用）
  const getContentReadInfo = async (
    userId: string,
    planId: string,
  ): Promise<{ [contentsType: string]: ContentReadInfo }> => {
    try {
      // Firestoreから取得（自動キャッシュされる）
      const data = await repository.getPlanContentReadInfo(userId, planId);

      // 各コンテンツタイプの初期化
      const result: { [contentsType: string]: ContentReadInfo } = {};
      for (const contentType of CONTENT_TYPE_LIST) {
        result[contentType] = data[contentType] || ContentReadInfo.empty();
      }

      // ローカル状態を更新
      latestContentsList.value[planId] = result;
      return result;
    } catch (error) {
      consoleError("Failed to get content read info:", error);
      // エラー時は空のデータを返す
      const emptyData: { [contentsType: string]: ContentReadInfo } = {};
      for (const contentType of CONTENT_TYPE_LIST) {
        emptyData[contentType] = ContentReadInfo.empty();
      }
      latestContentsList.value[planId] = emptyData;
      return emptyData;
    }
  };

  // 特定のコンテンツタイプの未読情報を更新
  const updateContentReadInfo = async (
    userId: string,
    planId: string,
    contentsType: string,
    checkInfo: ContentReadInfo,
  ) => {
    // ローカル状態を即座に更新
    if (!latestContentsList.value[planId]) {
      latestContentsList.value[planId] = {};
    }
    latestContentsList.value[planId][contentsType] = checkInfo;

    // Firestoreに更新（Firestoreキャッシュも自動更新される）
    try {
      await repository.setPlanContentReadInfo(userId, planId, latestContentsList.value[planId]);
    } catch (error) {
      consoleError("Failed to update content read info:", error);
    }
  };

  return {
    latestContentsList,
    getContentReadInfo,
    updateContentReadInfo,
  };
});
